/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable import/no-cycle */
import Vue from 'vue';
import axios from 'axios';
import { defineModule } from 'direct-vuex';

import { moduleGetterContext, moduleActionContext } from '@/store';
import { VideoUploadParams, Upload } from '@/types';

import { isAxiosError } from '@/api';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const uploadActionContext = ((context: any) => moduleActionContext(context, uploadModule));
const uploadGetterContext = (
  (args: [unknown, unknown, unknown, unknown]) => moduleGetterContext(args, uploadModule)
);

const uploadModule = defineModule({
  namespaced: true,
  state: {
    inProgress: false,
    dialogOpen: false,
    uploads: [] as Upload[],
  },
  getters: {
    totalProgress(...args): number {
      const { state } = uploadGetterContext(args);
      const validUploads = state.uploads.filter((upload) => !upload.cancelled);

      return Math.round(validUploads.reduce((total, upload) => total + (upload.finished ? 100 : upload.progress), 0) / validUploads.length);
    },
    allSuccessful(...args): boolean {
      const { state } = uploadGetterContext(args);
      return state.uploads.filter((upload) => !upload.error && !upload.cancelled).length === state.uploads.length;
    },
  },
  mutations: {
    setInProgress(state, val: boolean) {
      state.inProgress = val;
    },
    setDialogOpen(state, val: boolean) {
      state.dialogOpen = val;
    },
    setUploads(state, uploads: Upload[]) {
      state.uploads = uploads;
    },
    setUploadAtIndex(state, { upload, index }: { upload: Upload; index: number }) {
      Vue.set(state.uploads, index, upload);
    },
  },
  actions: {
    cancelUpload(context, index: number) {
      const { state, commit } = uploadActionContext(context);

      const currentUpload = state.uploads[index];

      currentUpload.cancelToken.cancel();

      const upload = { ...currentUpload, cancelled: true };
      commit.setUploadAtIndex({ index, upload });
    },
    cancelAllUploads(context) {
      const { state, dispatch } = uploadActionContext(context);

      state.uploads.forEach((_, i) => {
        dispatch.cancelUpload(i);
      });
    },
    async uploadVideos(context, videos: VideoUploadParams[]) {
      const { state, commit, rootState: { client } } = uploadActionContext(context);
      if (client === null) {
        return;
      }

      commit.setInProgress(true);
      function setUploadProgress(val: ProgressEvent, index: number) {
        const progress = (val.loaded / val.total) * 100;
        const currentUpload = state.uploads[index];
        const upload = { ...currentUpload, progress };
        commit.setUploadAtIndex({ upload, index });
      }

      const uploads: Upload[] = videos.map((video, index) => ({
        ...video,
        progress: 0,
        error: null,
        cancelled: false,
        finished: false,
        cancelToken: axios.CancelToken.source(),
        onProgress: (val: ProgressEvent) => { setUploadProgress(val, index); },
      }));

      commit.setUploads(uploads);

      // eslint-disable-next-line no-restricted-syntax
      for (const [index, upload] of uploads.entries()) {
        // Attempt to upload video
        let error: string | null = null;
        try {
          // eslint-disable-next-line no-await-in-loop
          error = await client.uploadVideo(upload);
        } catch (err) {
          // If not axios error, just rethrow
          if (!isAxiosError(err)) {
            throw err;
          }

          // Grab message
          error = err.response?.data?.message || err.message || err;
        }

        // Update upload
        commit.setUploadAtIndex({
          index,
          upload: {
            ...upload,
            error,
            finished: true,
          },
        });
      }

      commit.setInProgress(false);
    },
    resetUploadState(context) {
      const { commit } = uploadActionContext(context);
      commit.setInProgress(false);
      commit.setUploads([]);
    },
  },
});

export default uploadModule;
